import { map, finalize, catchError, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { DataServiceBase } from '../../../core/service/base/data.service.base';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { DocumentConnectionBase } from '../../../common/model/document-connection-base';

export abstract class ConnectedDocumentService<DocumentConnectionType extends DocumentConnectionBase> extends DataServiceBase {
  constructor(
    client: HttpClient,
    messageService: MessageService,
    progressBar: ProgressBarService,
    endPoint: string,
    @Inject(APP_CONFIG) config: IAppConfig
  ) {
    super(client, messageService, progressBar, config, endPoint);
  }

  add(model: DocumentConnectionType, supressSuccess?: boolean): Observable<DocumentConnectionType> {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(model));
    formData.append('document', model.document, model.document.name);
    this.progressBar.start();
    return this.client.post(this.endpoint, formData).pipe(
      tap(() => {
        if (!supressSuccess) this.notification.showSuccess();
      }),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  changeVisibilityLevel(form: any): Observable<DocumentConnectionType | any> {
    return this.client
      .post<DocumentConnectionType>(`${this.endpoint}/change-visibility`, JSON.stringify(form), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        tap(() => this.notification.showSuccess('SUCCESS.DOCUMENT_VISIBILITY_CHANGED'))
      );
  }

  delete(id: string): Observable<void> {
    this.progressBar.start();
    return this.client.delete<void>(`${this.endpoint}/${id}`).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  download(model: DocumentConnectionType): Observable<File> {
    this.progressBar.start();
    return this.getFile(`${this.endpoint}/download/${model.id}`, 'document').pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getByOwnerId(ownerId: string): Observable<DocumentConnectionType[]> {
    this.progressBar.start();
    return this.client.get<DocumentConnectionType[]>(`${this.endpoint}/${ownerId}`).pipe(
      map(items => {
        if (Array.isArray(items)) {
          return items.map(m => Object.assign(this.createModel(), m));
        }
        return [];
      }),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  abstract createModel(): DocumentConnectionType;
}
